import { useState, useCallback, useRef } from 'react';
import { AnalysisOrchestrator } from '../../services/analysis/AnalysisOrchestrator';
import {
  AnalysisStage,
  RecordingMetadata,
  AnalysisResult,
  MatchType,
  TimeSignature,
  SoundProfile,
  ActiveMission
} from '../../types';

// ✅ NEW: truth + narrative
import { buildTruthPayload } from '../../services/truth/buildTruthPayload';

export function useAnalysisPipeline(callbacks: {
  onSuccess: (res: AnalysisResult) => void;
  onError: (err: string) => void;
}) {
  const [stage, setStage] = useState<AnalysisStage>('IDLE');
  const [errorLabel, setErrorLabel] = useState<string | null>(null);
  const isBusy = useRef(false);

  const run = useCallback(
    async (params: {
      refFile: File | null;
      studentFile: File;
      targetBpm: number;
      latencyMs: number;
      lang: 'fr' | 'en';
      grid: string;
      levelId: number;
      matchType: MatchType;
      metadata?: RecordingMetadata;
      timeSignature: TimeSignature;
      soundProfile: SoundProfile;
      qualityWarning?: string;
      activeMission?: ActiveMission | null;
    }) => {
      if (isBusy.current) return;
      isBusy.current = true;

      setErrorLabel(null);
      setStage('DECODING');

      try {
        let result = await AnalysisOrchestrator.runFullLifecycle({
          ...params,
          onStageChange: (s, extra) => {
            setStage(s);
            if (extra) console.log(`[PIPELINE] ${s}: ${extra}`);
          }
        });

        // ✅ Attach TruthPayload + narrative (NO-SIM), never breaking the pipeline
        try {
          const truth = await buildTruthPayload(result);
          result = {
            ...result,
            feedback: {
              ...result.feedback,
              narrative: truth.outputs,
              truth_payload: truth
            }
          };
        } catch (e) {
          console.warn('[TRUTH] buildTruthPayload failed:', e);
        }

        // Back to idle once success is ready
        setStage('IDLE');
        callbacks.onSuccess(result);
      } catch (e: any) {
        console.error('[PIPELINE-ERROR]', e);
        const msg = e?.message || "Erreur d'analyse";
        setErrorLabel(msg);
        setStage('IDLE');
        callbacks.onError(msg);
      } finally {
        isBusy.current = false;
      }
    },
    [callbacks]
  );

  return {
    stage,
    run,
    isProcessing: stage !== 'IDLE' && stage !== 'DONE',
    errorLabel
  };
}
